import React, { ReactNode, useCallback, useMemo, useState } from 'react';
import { FilesWidget, FormValuesEnvelope } from '@truvity/widget__forms';
import { CredentialsMeta } from '@truvity/widget__client';
import { AnyObject, identity } from '@truvity/js-core__utils';

import { CredentialDid, ViewWidgetErrors, WalletDid, EntityDid, WidgetViewApi, WalletViewApi } from '../../types';
import { WalletForm } from '../WalletForm';
import { LinkedCredentialsPreviewRequests, LinkedCredentialsWidget } from '../../forms/widgets/linkedCredentials';
import { ReadonlyFilesStorage } from '../../forms/widgets/apiFilesStorage';
import { getNetworkErrorsDescriptor, isNotFound, isUnprocessableEntity } from '../../utils/error';
import { readonlyTheme } from '../../forms/readonlyTheme';
import { useCredentialSchema } from '../../hooks/useCredentialSchema';

type SlotProps = {
    meta?: CredentialsMeta;
};

const notImplemented = () => {
    throw new Error(`Not implemented`);
};

export type WalletViewCredentialProps = LinkedCredentialsPreviewRequests & {
    /**
     * The DID of a credential that we want to display.
     */
    credentialId: CredentialDid;

    /**
     * The DID of the wallet that stores the credential that we want to display.
     */
    walletDid: WalletDid;

    /**
     * The DID of the entity that owns the wallet where the credential we want to display is stored.
     */
    entityDid: EntityDid;

    /**
     * An API client that provides support for the necessary widget service operations required to work with
     * the credential that we want to display.
     */
    widgetApi: WidgetViewApi;

    /**
     * An API client that provides support for the necessary wallet service operations required to work with
     * the credential that we want to display.
     */
    walletApi: WalletViewApi;

    /**
     * A callback that notifies when a widget is fully displayed and ready for interactions.
     */
    onDisplayed?: () => void;

    /**
     * A callback that reports widget errors and allows for their handling.
     * If an error occurs, the widget will display an empty form without fields.
     */
    onError?: (error: ViewWidgetErrors) => void;

    /**
     * A render prop that displays content before all fields and inner controls.
     * The passed props include information about the credential metadata.
     */
    headerSlotRenderer?: (renderProps: SlotProps) => ReactNode;

    /**
     * A render prop that displays content after all fields and inner controls.
     * The passed props include information about the credential metadata.
     */
    footerSlotRenderer?: (renderProps: SlotProps) => ReactNode;
};

export const WalletViewCredential = ({
    credentialId,
    walletDid,
    entityDid,
    onDisplayed,
    getVcPreview,
    onError,
    widgetApi,
    walletApi,
    footerSlotRenderer,
    headerSlotRenderer,
}: WalletViewCredentialProps) => {
    const [meta, setMeta] = useState<CredentialsMeta>();

    const handleNetworkErrors = useCallback(
        (error: any): void => {
            if (!onError) return;

            if (isNotFound(error)) {
                onError({
                    code: 'ERRCREDENTIALNOTFOUND',
                    credentialId,
                });
            } else if (isUnprocessableEntity(error)) {
                onError({
                    code: 'ERRCREDENTIALNOTSUPPORTED',
                    credentialId,
                });
            } else {
                onError(getNetworkErrorsDescriptor(error));
            }
        },
        [credentialId, onError],
    );

    const getJsonSchema = useCallback(
        async (): Promise<AnyObject> => widgetApi.getCredentialSchema(credentialId, walletDid, entityDid),
        [credentialId, entityDid, walletDid, widgetApi],
    );

    const credentialSchema = useCredentialSchema({
        getJsonSchema,
        onError: handleNetworkErrors,
    });

    const { getForm } = useMemo(
        () => ({
            getForm: async (): Promise<FormValuesEnvelope> => {
                try {
                    const credential = await widgetApi.getCredential(credentialId, walletDid, entityDid);

                    setMeta(credential.meta);

                    if (credentialSchema.state !== 'loaded') {
                        throw new Error(`The credential schema for this document is not available`);
                    }

                    return {
                        values: credentialSchema.jsonToJs(credential.claims),
                        revision: 0,
                    };
                } catch (error) {
                    handleNetworkErrors(error);

                    setMeta(undefined);

                    return {
                        values: {},
                        revision: 0,
                    };
                }
            },
        }),
        [widgetApi, credentialId, walletDid, entityDid, credentialSchema, handleNetworkErrors],
    );

    if (credentialSchema.state !== 'loaded') return null;

    return (
        <div>
            <WalletForm
                theme={readonlyTheme}
                entityId={credentialId}
                defs={credentialSchema.schema}
                convertToApiType={identity}
                getForm={getForm}
                onInit={onDisplayed}
                footerSlot={() => <>{footerSlotRenderer?.({ meta })}</>}
                headerSlot={() => <>{headerSlotRenderer?.({ meta })}</>}
                widgets={[
                    new FilesWidget(new ReadonlyFilesStorage(walletApi, walletDid, entityDid)),
                    new LinkedCredentialsWidget({
                        walletDid,
                        entityDid,
                        walletApi,
                        widgetApi,
                        getVcPreview,
                        suggestVc: notImplemented,
                        onError,
                    }),
                ]}
            />
        </div>
    );
};
